import { useMemo } from 'react';
import { Button, Input, Select, Space, Typography } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { parse } from '../../utils/kql';
import { CLOSE, PLUS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import TokenInput from '../common/TokenInput';
import ConditionBuilder from '../common/ConditionBuilder';
import { normalizeId } from '../../utils/ids';
import { cardAndDescendantIds } from '../../utils/facets';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { choiceListOf } from '../../utils/choiceValues';

/**
 * The card-level **worklist** config: what a LIST of this card's items looks
 * like when the job is to work through them rather than read them — a
 * medication pass, an inspection round, a cycle count, an attendance roll.
 *
 * Authored here rather than on the list itself because it is a property of
 * the CARD (it names that card's elements), and because a worklist is the
 * kind of thing one person sets up and a shift then uses without thinking
 * about it.
 *
 * The questions, in the order they matter:
 *   1. which element one tap sets, and to what        (the whole point)
 *   2. what else a row can be set to, and what that   (the exception path,
 *      additionally requires                           which is the entry
 *                                                      that matters later)
 *   3. how rows group, what counts as outstanding, and what gets stamped
 *      on everything the pass writes
 *
 * The API validates the same shape at write time and answers with
 * field-level messages, so this editor builds the documented JSON and does
 * not re-implement the rules.
 */

const { Text } = Typography;

// The values a stamp can take, offered by the label a person reads; the stored
// value stays the template string the pass resolves on save.
const STAMP_TOKENS = [
	{ token: '{{now}}', label: 'Now' },
	{ token: '{{today}}', label: 'Today' },
	{ token: '{{user.name}}', label: 'Your name' },
	{ token: '{{user.email}}', label: 'Your email' },
];

const labelOf = el => el?.label?.singular || el?.name || '';

// What one tap can write: a value has to be pickable.
const ACTION_TYPES = new Set([ 'choice', 'text-small', 'text-large' ]);

// A Choice's list only (KPE-033): a short text that carries one (kept for a
// change back to Choice) is written by typing, like any text.
const choiceValuesOf = element => {
	const choices = choiceListOf(element);
	return choices
		.map(choice => (typeof choice === 'string' ? choice : choice?.value))
		.filter(Boolean);
};

export default function WorklistOptions({ worklist, elements = [], onChange, disabled = false, cardId = null }) {

	const enabled = !!worklist;
	const config = worklist || {};
	const action = config.action || {};

	const named = useMemo(() => (elements || []).filter(el => el?.name), [ elements ]);
	// The collection whose tags and items the builder's rows may pick from: the
	// card's own, and only when it has ONE. A global card's worklist is read in
	// every collection that uses it, so a tag or an item of this collection
	// would mean nothing there; its rows keep a text box and offer no Tags.
	const cardDefinitions = useOptionalCardDefinitions();
	const ownerCollectionId = useMemo(() => {
		const card = cardId ? cardDefinitions?.cardsById?.[normalizeId(cardId)] : null;
		return card?.scope === 'collection' ? normalizeId(card.collection_id) || null : null;
	}, [ cardId, cardDefinitions?.cardsById ]);
	// The worklist reads its card AND the cards beneath it (a pin covers its
	// descendants), so a condition may name an element only a child card holds
	// (the P4 review's R-WL1): those are offered too, after this card's own, and
	// an id a child holds reads as its label rather than as gone. A name both
	// have is this card's (the builder keeps the first).
	const filterElements = useMemo(() => {
		if (!cardId || !cardDefinitions?.cards || typeof cardDefinitions.getElementsForCard !== 'function') return named;
		const ownNames = new Set(named.map(el => el.name));
		const below = cardAndDescendantIds(cardDefinitions.cards, cardId)
			.filter(id => id !== normalizeId(cardId))
			.flatMap(id => cardDefinitions.getElementsForCard(id) || [])
			.filter(el => el?.name && !ownNames.has(el.name));
		return below.length ? [...named, ...below] : named;
	}, [ named, cardId, cardDefinitions ]);
	const actionCandidates = useMemo(() => named.filter(el => ACTION_TYPES.has(el.dataType)), [ named ]);
	const actionOptions = useMemo(
		() => actionCandidates.map(el => ({ value: el.name, label: labelOf(el) })),
		[ actionCandidates ]
	);
	// Seeded with an element that already OFFERS values — a choice is what one
	// tap writes, and a card that has one almost always means that one.
	const seedElement = useMemo(
		() => (actionCandidates.find(el => choiceValuesOf(el).length) || actionCandidates[ 0 ])?.name || '',
		[ actionCandidates ]
	);
	// Never a file element (PR 10b): a worklist neither groups rows by an
	// attachment id, nor asks for or stamps a file in a row.
	// Nor a repeating group (KPR-679): a list of entries is neither a group
	// key nor a value a pass asks for or stamps.
	const allOptions = useMemo(() => named.filter(el => el.dataType !== 'file' && el.dataType !== 'repeating-group').map(el => ({ value: el.name, label: labelOf(el) })), [ named ]);
	// What a pass may ask for or stamp: never a "Me too" count (KPR-390) —
	// nobody types it and only the people who confirm move it. Rows may
	// still be grouped by one.
	const writableOptions = useMemo(() => named.filter(el => el.dataType !== 'file' && el.dataType !== 'confirmations' && el.dataType !== 'repeating-group').map(el => ({ value: el.name, label: labelOf(el) })), [ named ]);
	const actionElement = named.find(el => el.name === action.element);
	const valueOptions = choiceValuesOf(actionElement).map(value => ({ value, label: value }));

	const patch = next => onChange({ ...config, ...next });
	const patchAction = next => onChange({ ...config, action: { ...action, ...next } });

	const stampRows = Object.entries(config.stamp || {});

	// A filter that will not parse cannot narrow anything, and the list refuses
	// to draw a pass it cannot honour — so the author has to hear it HERE.
	// (The API exempts filter text that is byte-identical to what is stored, so
	// an unreadable one written before that gate survives every later save; it
	// is this editor or nobody.) The grammar is the only judge worth asking.
	const filterError = useMemo(() => {
		const text = `${ config.filter || '' }`.trim();
		if (!text) return '';
		try {
			parse(text);
			return '';
		} catch (e) {
			return e?.message || 'This filter could not be read.';
		}
	}, [ config.filter ]);

	return (<>
		<ToggleRow
			label='Worklist'
			description={'A list of these items you work through — one tap per row, one save for the pass. '
				+ 'Offered on the items list whenever it holds only this card.'}
			checked={enabled}
			disabled={disabled}
			onChange={value => onChange(value
				? { action: { element: seedElement, primary: '' } }
				: false)}
		/>

		{enabled ? (
			<div className='toggle-row--child worklist-options'>
				<Space direction='vertical' size={12} style={{ width: '100%' }}>

					<div>
						<label htmlFor='worklistActionElement' className='d-block'>One tap sets</label>
						<Space wrap align='start'>
							<Select
								id='worklistActionElement'
								style={{ minWidth: 200 }}
								value={action.element || undefined}
								options={actionOptions}
								placeholder='Which element?'
								disabled={disabled}
								onChange={value => patchAction({ element: value, primary: '', alternates: undefined })}
							/>
							{valueOptions.length ? (
								<Select
									style={{ minWidth: 180 }}
									value={action.primary || undefined}
									options={valueOptions}
									placeholder='To what value?'
									disabled={disabled || !action.element}
									aria-label='Primary value'
									onChange={value => patchAction({ primary: value })}
								/>
							) : (
								<Input
									{...NO_TEXT_ASSIST}
									style={{ minWidth: 180 }}
									value={action.primary || ''}
									placeholder='To what value?'
									aria-label='Primary value'
									disabled={disabled || !action.element}
									onChange={event => patchAction({ primary: event.target.value })}
								/>
							)}
						</Space>
					</div>

					<div>
						<label htmlFor='worklistAlternates' className='d-block'>Other outcomes</label>
						<Select
							id='worklistAlternates'
							mode={valueOptions.length ? 'multiple' : 'tags'}
							style={{ width: '100%' }}
							value={action.alternates || []}
							options={valueOptions.filter(option => option.value !== action.primary)}
							placeholder='Every other value this element offers'
							disabled={disabled || !action.element}
							onChange={value => patchAction({ alternates: value.length ? value : undefined })}
						/>
						<Text type='secondary'>Left empty, every other value the element offers.</Text>
					</div>

					<div>
						<label htmlFor='worklistRequire' className='d-block'>An exception also needs</label>
						<Select
							id='worklistRequire'
							style={{ width: '100%' }}
							allowClear
							value={action.onAlternate?.require || undefined}
							options={writableOptions}
							placeholder='Nothing else'
							disabled={disabled || !action.element}
							onChange={value => patchAction({ onAlternate: value ? { require: value } : undefined })}
						/>
						<Text type='secondary'>
							Asked when a row is set to anything but the primary value — the &ldquo;why&rdquo; is the entry that matters later.
						</Text>
					</div>

					<div>
						<label htmlFor='worklistGroupBy' className='d-block'>Group rows by</label>
						<Select
							id='worklistGroupBy'
							style={{ width: '100%' }}
							allowClear
							value={config.groupBy || undefined}
							options={allOptions}
							placeholder='One flat list'
							disabled={disabled}
							onChange={value => patch({ groupBy: value || undefined })}
						/>
					</div>

					{/* The id is the card form's scroll target when it refuses this filter. */}
					<div id='worklistFilter' role='group' aria-labelledby='worklistFilterLabel'>
						<Text strong id='worklistFilterLabel' className='d-block'>Outstanding means</Text>
						<ConditionBuilder
							value={config.filter || ''}
							onChange={value => patch({ filter: value || undefined })}
							elements={filterElements}
							collectionId={ownerCollectionId || undefined}
							disabled={disabled}
							// The list is filtered as the person looking at it, so a
							// person row can say "Me" (keepr-api applies a worklist as
							// that reader's own query, and refuses nothing about it).
							allowMe
							emptyText='No conditions yet. Add one to say what is still outstanding.'
						/>
						<Text type='secondary' className='d-block'>Left empty, the list shows whatever it is already filtered to.</Text>
						{filterError ? (
							<Text type='danger' className='d-block'>
								This filter could not be read. Until it reads, the worklist will not be offered on the list.
							</Text>
						) : null}
					</div>

					<div>
						<Text strong className='d-block'>Stamp on every row the pass saves</Text>
						{stampRows.map(([ name, value ]) => (
							<Space key={name} align='start' style={{ display: 'flex', marginBottom: 8 }}>
								<Select
									style={{ width: 200 }}
									value={name}
									options={writableOptions}
									disabled={disabled}
									aria-label='Stamped element'
									onChange={next => {
										const stamp = { ...config.stamp };
										delete stamp[ name ];
										stamp[ next ] = value;
										patch({ stamp });
									}}
								/>
								<div style={{ minWidth: 200 }}>
									<TokenInput
										value={value}
										tokens={STAMP_TOKENS}
										placeholder='Type a value, or pick Now from the menu'
										ariaLabel={`Stamped value for ${ name }`}
										disabled={disabled}
										onChange={next => patch({ stamp: { ...config.stamp, [ name ]: next } })}
									/>
								</div>
								<Button
									type='text'
									danger
									icon={<CLOSE />}
									aria-label={`Remove the ${ name } stamp`}
									disabled={disabled}
									onClick={() => {
										const stamp = { ...config.stamp };
										delete stamp[ name ];
										patch({ stamp: Object.keys(stamp).length ? stamp : undefined });
									}}
								/>
							</Space>
						))}
						<Button
							size='small'
							type='dashed'
							icon={<PLUS />}
							disabled={disabled}
							onClick={() => {
								const free = writableOptions.find(option => !(config.stamp || {})[ option.value ]);
								if (!free) return;
								patch({ stamp: { ...config.stamp, [ free.value ]: '{{now}}' } });
							}}
						>
							Add a stamp
						</Button>
						<Text type='secondary' className='d-block'>
							Pick Now, Today, Your name or Your email from the menu; each fills in when the pass is saved.
						</Text>
					</div>

				</Space>
			</div>
		) : null}
	</>);
}
